import axios from 'axios';
import config from '@appConfig'

/**
 * RFC 6266 / RFC 5987: use filename* (UTF-8 percent-encoded) when present, else legacy filename=.
 */
function contentDispositionFilename(contentDisposition) {
    if (!contentDisposition || typeof contentDisposition !== 'string') {
        return null;
    }
    const cd = contentDisposition.trim();
    const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
    if (star && star[1]) {
        try {
            return decodeURIComponent(star[1].trim());
        } catch (e) {
            // fall through to filename=
        }
    }
    const quoted = cd.match(/filename="((?:\\.|[^"\\])*)"/);
    if (quoted && quoted[1]) {
        return quoted[1].replace(/\\"/g, '"').replace(/\\\\/g, '\\');
    }
    const plain = cd.match(/filename=([^;\n]+)/);
    if (plain && plain[1]) {
        return plain[1].trim().replace(/^["']|["']$/g, '');
    }
    return null;
}

class Api {

    instance = null;

    defaultConfig = {
        baseURL: `${config.API_URL}/`,
    };

    constructor(config) {
        this.createInstance(config);
    }

    createInstance(config) {
        const instanceConfig = config || this.defaultConfig;
        this.instance = axios.create(instanceConfig);

        if (!this.instance.defaults.headers) {
            this.instance.defaults.headers = {};
        }
        if (!this.instance.defaults.headers.common) {
            this.instance.defaults.headers.common = {};
        }

        this.instance.defaults.headers.common['Accept-Language'] = window.locale || 'ru';
        this.instance.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
        this.instance.defaults.withCredentials = true; // With this setup axios send cookies
        let token = document.head.querySelector('meta[name="csrf-token"]');

        if (token) {
            this.instance.defaults.headers.common['X-CSRF-TOKEN'] = token.content;
        }
    }

    getUri(uri, data = null) {
        return this.instance.getUri({
            url: config.API_URL + '/' + uri,
            method: 'get',
            params: data
        });
    }

    async get(uri, data = null) {
        return this.request(async (_uri, _data) => this.instance.get(_uri, {
            params: _data
        }), uri, data)
    }

    async post(uri, data = null) {
        return this.request(async (_uri, _data) => this.instance.post(_uri, _data), uri, data);
    }

    async put(uri, data = null) {
        return this.request(async (_uri, _data) => this.instance.put(_uri, _data), uri, data);
    }

    async patch(uri, data = null) {
        return this.request(async (_uri, _data) => this.instance.patch(_uri, {
            params: _data
        }), uri, data)
    }

    async delete(uri, data = null) {
        return this.request(async (_uri, _data) => this.instance.delete(_uri, {
            data: _data
        }), uri, data);
    }

    async postBlob(uri, data = null) {
        let response = {};
        try {
            response = await this.instance.post(uri, data, {
                responseType: 'blob'
            });
        } catch (e) {
            if (typeof e.response === 'undefined') {
                throw new Error("Вероятно есть cетевые проблемы. Проверьте подключение или попробуйте VPN.");
            }
            throw e.response.data;
        }
        const cd = response.headers['content-disposition']
            ?? response.headers['Content-Disposition']
            ?? null;

        return {
            blob: response.data,
            headers: response.headers,
            filename: contentDispositionFilename(cd),
        };
    }

    async request(func, uri, data = null) {
        let response = {};

        try {
            response = await func(uri, data);
        } catch (e) {
            if (typeof e.response === 'undefined') {
                throw new Error("Вероятно есть cетевые проблемы. Проверьте подключение или попробуйте VPN.");
            }
            throw e.response.data;
        }

        return response.data || {};
    }
}

export default Api;
